import axios from 'axios';
import { API } from '../App';

// Simple in-memory cache with TTL
const cache = new Map();
const CACHE_TTL = {
  topics: 5 * 60 * 1000,      // 5 minutes
  landing: 10 * 60 * 1000,    // 10 minutes
  leaderboard: 2 * 60 * 1000, // 2 minutes
  user: 1 * 60 * 1000,        // 1 minute
  dashboard: 3 * 60 * 1000,   // 3 minutes - for dashboard data
  default: 30 * 1000          // 30 seconds
};

// Get cache key
const getCacheKey = (url, params = {}) => {
  return `${url}:${JSON.stringify(params)}`;
};

// Check if cache is valid
const isCacheValid = (key, ttl) => {
  const cached = cache.get(key);
  if (!cached) return false;
  return Date.now() - cached.timestamp < ttl;
};

// Cached GET request
export const cachedGet = async (url, options = {}) => {
  const { ttl = CACHE_TTL.default, forceRefresh = false, params = {} } = options;
  const cacheKey = getCacheKey(url, params);
  
  // Return cached data if valid and not forcing refresh
  if (!forceRefresh && isCacheValid(cacheKey, ttl)) {
    return cache.get(cacheKey).data;
  }
  
  try {
    const response = await axios.get(url, { 
      withCredentials: true,
      params 
    });
    
    // Store in cache
    cache.set(cacheKey, {
      data: response.data,
      timestamp: Date.now()
    });
    
    return response.data;
  } catch (error) {
    // Return stale cache if request fails
    const stale = cache.get(cacheKey);
    if (stale) return stale.data;
    throw error;
  }
};

// Clear specific cache
export const clearCache = (urlPattern) => {
  if (!urlPattern) {
    cache.clear();
    return;
  }
  
  for (const key of cache.keys()) {
    if (key.includes(urlPattern)) {
      cache.delete(key);
    }
  }
};

// Preload critical data
export const preloadData = async () => {
  try {
    // Preload topics in background
    cachedGet(`${API}/topics`, { ttl: CACHE_TTL.topics });
    
    // Preload landing content
    cachedGet(`${API}/landing-content`, { ttl: CACHE_TTL.landing });
  } catch (error) {
    console.log('Preload failed silently');
  }
};

// Batch multiple requests
export const batchRequests = async (requests) => {
  return Promise.all(
    requests.map(({ url, options }) => 
      cachedGet(url, options).catch(err => ({ error: err }))
    )
  );
};

// API helper with caching
export const api = {
  // Topics - cached for 5 minutes
  getTopics: (forceRefresh = false) => 
    cachedGet(`${API}/topics`, { ttl: CACHE_TTL.topics, forceRefresh }),
  
  // Landing content - cached for 10 minutes (with language support)
  getLandingContent: (lang = 'en', forceRefresh = false) => 
    cachedGet(`${API}/landing-content`, { ttl: CACHE_TTL.landing, forceRefresh, params: { lang } }),
  
  // User data - cached for 1 minute
  getCurrentUser: (forceRefresh = false) => 
    cachedGet(`${API}/auth/me`, { ttl: CACHE_TTL.user, forceRefresh }),
  
  // Leaderboard - cached for 2 minutes
  getLeaderboard: (params = {}) => 
    cachedGet(`${API}/leaderboard`, { ttl: CACHE_TTL.leaderboard, params }),
  
  // Gamification stats - cached for 1 minute
  getGamificationStats: () => 
    cachedGet(`${API}/gamification/stats`, { ttl: CACHE_TTL.user }),
  
  // Dashboard data - cached for 3 minutes
  // OPTIMIZED: Only loads essential data first (user + topics)
  // Deferred data (announcements, smart insights) loads after initial render
  getDashboardData: async (forceRefresh = false) => {
    const cacheKey = 'dashboard_data';
    
    // Check cache first - instant return if cached
    if (!forceRefresh && cache.has(cacheKey)) {
      const cached = cache.get(cacheKey);
      if (Date.now() - cached.timestamp < CACHE_TTL.dashboard) {
        return cached.data;
      }
    }
    
    // FAST PATH: Only fetch essential data (user + enrolled topics)
    const [user, enrolledRes] = await Promise.all([
      cachedGet(`${API}/auth/me`, { ttl: CACHE_TTL.user, forceRefresh }),
      axios.get(`${API}/user/enrolled-topics`, { withCredentials: true })
    ]);
    
    const data = {
      user,
      enrolledTopics: enrolledRes.data.enrolled || [],
      suggestedTopics: enrolledRes.data.suggested || [],
      siteSettings: { site_name: 'ikizamini' }, // Use default, load async
      smartData: null, // Load async
      announcements: [] // Load async
    };
    
    // Store in cache
    cache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });
    
    return data;
  },
  
  // Load deferred dashboard data (non-critical)
  getDashboardDeferredData: async () => {
    const [siteRes, smartRes, announcementsRes] = await Promise.all([
      cachedGet(`${API}/landing-content`, { ttl: CACHE_TTL.landing }).catch(() => ({ site_name: 'ikizamini' })),
      axios.get(`${API}/analytics/smart-dashboard`, { withCredentials: true }).catch(() => ({ data: null })),
      axios.get(`${API}/announcements/active`, { withCredentials: true }).catch(() => ({ data: [] }))
    ]);
    
    return {
      siteSettings: siteRes,
      smartData: smartRes.data || smartRes,
      announcements: announcementsRes.data || announcementsRes || []
    };
  },
  
  // Invalidate dashboard cache (call after actions that change dashboard data)
  invalidateDashboard: () => {
    cache.delete('dashboard_data');
  },

  // Clear all caches
  clearAll: () => clearCache(),
  
  // Clear user-related caches (after login/logout)
  clearUserCache: () => {
    clearCache('/auth/');
    clearCache('/gamification/');
    clearCache('/analytics/');
  },
  
  // Clear landing content cache (after CMS update)
  clearLandingCache: () => {
    clearCache('/landing-content');
  }
};

export default api;
